/**
 * Biopet Essential page-4 calculation helpers.
 *
 * Logic:
 * 1) Use raw CSV values for the listed species.
 * 2) Compute deltaCT = speciesValue - universal.
 * 3) Compare deltaCT with quartile thresholds (Q2, Q3).
 * 4) Return render-ready rows (species name + bar color + arrow-down metadata).
 */

export const BIOPET_ESSENTIAL_SPECIES_GROUPS = {
  probiotics: [
    'Bifidobacterium infantis',
    'Enterococcus faecium',
    'Lactobacillus acidophilus',
    'Lactobacillus paracasei',
    'Lactobacillus plantarum',
    'Lactobacillus rhamnosus',
    'Streptococcus thermophilus'
  ],
  core: [
    'Faecalibacterium',
    'Peptacetobacter hiranomis'
  ],
  harmful: [
    'Escherichia coli',
    'Streptococcus'
  ]
};

/**
 * Absolute position config (page 4) per species row.
 * You can tune each element independently:
 * - name: species text position
 * - bar: segmented color bar position + size
 *   - width (number, px)
 *   - height (number, px)
 * - label: result text position (Normal / Deficient / etc.)
 *   - supports style props: fontSize (number, px)
 * - name supports style props:
 *   - fontSize (number, px)
 *   - italic (boolean)
 *
 * Units are px (relative to your page-4 overlay container origin).
 */
export const BIOPET_ESSENTIAL_PAGE4_POSITION = {
  page: 4,
  probiotics: {
    startY: 0,
    rowGap: 34,
    items: {
      'Bifidobacterium infantis': {
        name: { x: -62, y: -235, fontSize: 15, italic: true },
        bar: { x: 150, y: -235, width: 320, height: 17 },
        label: { x: 505, y: -232, fontSize: 16}
      },
      'Enterococcus faecium': {
        name: { x: -62, y: -195, fontSize: 15, italic: true },
        bar: {  x: 150, y: -196, width: 320, height: 17 },
        label: { x: 505, y: -192, fontSize: 16}
      },
      'Lactobacillus acidophilus': {
        name: { x: -62, y: -155, fontSize: 15, italic: true },
        bar: {  x: 150, y: -157, width: 320, height: 17 },
        label: { x: 505, y: -153, fontSize: 16}
      },
      'Lactobacillus paracasei': {
        name: { x: -62, y: -115, fontSize: 15, italic: true },
        bar: {  x: 150, y: -118, width: 320, height: 17 },
        label: { x: 505, y: -114, fontSize: 16}
      },
      'Lactobacillus plantarum': {
        name: { x: -62, y: -76, fontSize: 15, italic: true },
        bar: {  x: 150, y: -79, width: 320, height: 17 },
        label: { x: 505, y: -74, fontSize: 16}
      },
      'Lactobacillus rhamnosus': {
        name: { x: -62, y: -37, fontSize: 15, italic: true },
        bar: {  x: 150, y: -40, width: 320, height: 17 },
        label: { x: 505, y: -34, fontSize: 16}
      },
      'Streptococcus thermophilus': {
        name: { x: -62, y: 2, fontSize: 15, italic: true },
        bar: { x: 150, y: -1, width: 320, height: 17 },
        label: { x: 505, y: 4, fontSize: 16 }
      }
    }
  },
  core: {
    startY: 0,
    rowGap: 34,
    items: {
      'Faecalibacterium': {
        name: { x: -62, y: -365, fontSize: 15, italic: true },
        bar: { x: 150, y: -368, width: 320, height: 17 },
        label: { x: 505, y: -365, fontSize: 16 }
      },
      'Peptacetobacter hiranomis': {
        name: { x: -62, y: -325, fontSize: 15, italic: true },
        bar: { x: 150, y: -328, width: 320, height: 17 },
        label: { x: 505, y: -323, fontSize: 16 }
      }
    }
  },
  harmful: {
    startY: 0,
    rowGap: 34,
    items: {
      'Escherichia coli': {
        name: { x: -62, y: -400, fontSize: 15, italic: true },
        bar: { x: 150, y: -400, width: 320, height: 17 },
        label: { x: 505, y: -397, fontSize: 16 }
      },
      'Streptococcus': {
        name: { x: -62, y: -360, fontSize: 15, italic: true },
        bar: { x: 150, y: -360, width: 320, height: 17 },
        label: { x: 505, y: -357, fontSize: 16 }
      }
    }
  }
};

/**
 * Keep this list if you want to render the full palette legend left -> right.
 */
export const BIOPET_BAR_GRADIENT_PALETTE = [
  'linear-gradient(90deg, #5969b1 0%, #80caeb 100%)',
  'linear-gradient(90deg, #1ba5c8 0%, #87ceb3 100%)',
  '#8dc63f',
  'linear-gradient(90deg, #febc11 0%, #f05773 100%)',
  'linear-gradient(90deg, #f05773 0%, #d11f43 100%)'
];

/**
 * Status -> bar fill. (3-state mapping using your provided palette family)
 */
const STATUS_TO_BAR = {
  good: '#8dc63f',
  warning: 'linear-gradient(90deg, #febc11 0%, #f05773 100%)',
  bad: 'linear-gradient(90deg, #f05773 0%, #d11f43 100%)'
};

/**
 * Map report age (years) to quartile life-stage column (Modify Quartile Value grid).
 * Intervals match the UI: Young | Adult | Senior columns (row-major Q1–Q3).
 * - Young:  [0, 1)   — under 1 year (exclusive at 1 so Adult 1–7 does not overlap)
 * - Adult:  [1, 7]
 * - Senior: (7, ∞)
 */
export function getBiopetAgeStage(ageYearsRaw) {
  const age = parseFloat(String(ageYearsRaw ?? '').trim());
  if (!Number.isFinite(age) || age < 0) return 'Adult';
  if (age < 1) return 'Young';
  if (age <= 7) return 'Adult';
  return 'Senior';
}

function toNumber(value) {
  const n = parseFloat(String(value ?? '').trim());
  return Number.isFinite(n) ? n : 0;
}

function toFiniteOr(value, fallback) {
  const n = parseFloat(String(value ?? '').trim());
  return Number.isFinite(n) ? n : fallback;
}

function quartileColKey(ageStage) {
  if (ageStage === 'Young') return 'young';
  if (ageStage === 'Senior') return 'senior';
  return 'adult';
}

function resolvePage4Position(group, speciesName, speciesIndex) {
  const groupCfg = BIOPET_ESSENTIAL_PAGE4_POSITION[group] || {};
  const rowGap = Number.isFinite(groupCfg.rowGap) ? groupCfg.rowGap : 34;
  const rowY = (Number.isFinite(groupCfg.startY) ? groupCfg.startY : 0) + (speciesIndex * rowGap);
  const itemCfg = groupCfg.items?.[speciesName] || {};

  const name = itemCfg.name || {};
  const bar = itemCfg.bar || {};
  const label = itemCfg.label || {};

  return {
    name: {
      x: toFiniteOr(name.x, 0),
      y: toFiniteOr(name.y, rowY),
      fontSize: toFiniteOr(name.fontSize, 18),
      italic: !!name.italic
    },
    bar: {
      x: toFiniteOr(bar.x, 360),
      y: toFiniteOr(bar.y, rowY),
      width: toFiniteOr(bar.width, 420),
      height: toFiniteOr(bar.height, 24)
    },
    label: {
      x: toFiniteOr(label.x, 800),
      y: toFiniteOr(label.y, rowY),
      fontSize: toFiniteOr(label.fontSize, 30)
    }
  };
}

/**
 * quartileBySpecies shape (recommended):
 * {
 *   "Bifidobacterium infantis": {
 *     q2: { young: number, senior: number, adult: number },
 *     q3: { young: number, senior: number, adult: number }
 *   },
 *   ...
 * }
 *
 * If a species is missing in quartileBySpecies, fallback values are used.
 */

/**
 * @param {Record<string, string[][]|number[][]>|null|undefined} quartileGridBySpecies - species -> 3×3 grid (rows Q1–Q3, cols Young, Adult, Senior)
 * @returns {Record<string, { q2: {young,adult,senior}, q3: {...} }>}
 */
export function buildQuartileBySpeciesFromGrids(quartileGridBySpecies) {
  const out = {};
  if (!quartileGridBySpecies || typeof quartileGridBySpecies !== 'object') return out;
  for (const [speciesName, grid] of Object.entries(quartileGridBySpecies)) {
    if (!grid || !Array.isArray(grid) || grid.length < 3) continue;
    const r2 = grid[1];
    const r3 = grid[2];
    if (!Array.isArray(r2) || !Array.isArray(r3) || r2.length < 3 || r3.length < 3) continue;
    out[speciesName] = {
      q2: {
        young: toNumber(r2[0]),
        adult: toNumber(r2[1]),
        senior: toNumber(r2[2])
      },
      q3: {
        young: toNumber(r3[0]),
        adult: toNumber(r3[1]),
        senior: toNumber(r3[2])
      }
    };
  }
  return out;
}

export function evaluateBiopetEssentialRow({
  speciesName,
  group,
  speciesIndex = 0,
  speciesValue,
  universalValue,
  ageStage,
  quartileBySpecies,
  fallbackQ2 = 0,
  fallbackQ3 = 0
}) {
  const deltaCT = toNumber(speciesValue) - toNumber(universalValue);
  const col = quartileColKey(ageStage);
  const speciesQ = quartileBySpecies?.[speciesName];

  const q2 = toNumber(speciesQ?.q2?.[col] ?? fallbackQ2);
  const q3 = toNumber(speciesQ?.q3?.[col] ?? fallbackQ3);

  // Probiotics/Core:
  // - Normal: delta <= Q2
  // - Slightly deficient: Q2 < delta < Q3
  // - Deficient: delta >= Q3
  //
  // Harmful:
  // - Excessive: delta <= Q2
  // - Slightly excessive: Q2 < delta < Q3
  // - Normal: delta >= Q3
  let label = 'Normal';
  let statusLevel = 'good';

  if (group === 'harmful') {
    if (deltaCT <= q2) {
      label = 'Excessive';
      statusLevel = 'bad';
    } else if (deltaCT < q3) {
      label = 'Slightly excessive';
      statusLevel = 'warning';
    } else {
      label = 'Normal';
      statusLevel = 'good';
    }
  } else {
    if (deltaCT <= q2) {
      label = 'Normal';
      statusLevel = 'good';
    } else if (deltaCT < q3) {
      label = 'Slightly deficient';
      statusLevel = 'warning';
    } else {
      label = 'Deficient';
      statusLevel = 'bad';
    }
  }

  return {
    speciesName,
    group,
    rawValue: toNumber(speciesValue),
    universal: toNumber(universalValue),
    deltaCT,
    q2,
    q3,
    label,
    statusLevel,
    // For page-4 bar rendering
    barColor: STATUS_TO_BAR[statusLevel],
    showArrowDown: true,
    position: resolvePage4Position(group, speciesName, speciesIndex)
  };
}

/**
 * Build page-4 rows for all configured species.
 */
export function buildBiopetEssentialPage4Results({
  rowData,
  quartileBySpecies,
  ageYears
}) {
  const ageStage = getBiopetAgeStage(ageYears ?? rowData?.age);
  const universal = toNumber(rowData?.universal ?? rowData?.['Universal bacteria']);
  const results = [];

  Object.entries(BIOPET_ESSENTIAL_SPECIES_GROUPS).forEach(([group, speciesList]) => {
    speciesList.forEach((speciesName, speciesIndex) => {
      results.push(
        evaluateBiopetEssentialRow({
          speciesName,
          group,
          speciesIndex,
          speciesValue: rowData?.[speciesName],
          universalValue: universal,
          ageStage,
          quartileBySpecies,
          fallbackQ2: 0,
          fallbackQ3: 0
        })
      );
    });
  });

  return {
    ageStage,
    universal,
    results
  };
}
